/**
 * Platform status registry, preserving the complete ELC P0 token contract.
 *
 * One registry so a status means the same colour on every platform screen,
 * and every tone resolves to a `-dark` text token on a `-light`
 * background token from styles/tokens.css. Those pairs flip with the
 * theme and meet WCAG AA (≥ 4.5:1) in light and dark; that is asserted
 * by the platform regression tests against the actual token values.
 */

import type { Tone } from './types';

export type StatusTone = Tone;

export const STATUS_TONES: readonly StatusTone[] = [
  'neutral', 'info', 'success', 'warning', 'danger', 'accent',
];

/** CSS custom-property names per tone (no `var()`), defined in tokens.css. */
export const TONE_TOKEN_NAMES: Record<StatusTone, { fg: string; bg: string; border: string }> = {
  neutral: { fg: '--color-neutral-dark', bg: '--color-neutral-light', border: '--color-neutral' },
  info: { fg: '--color-info-dark', bg: '--color-info-light', border: '--color-info' },
  success: { fg: '--color-success-dark', bg: '--color-success-light', border: '--color-success' },
  warning: { fg: '--color-warning-dark', bg: '--color-warning-light', border: '--color-warning' },
  danger: { fg: '--color-danger-dark', bg: '--color-danger-light', border: '--color-danger' },
  accent: { fg: '--color-accent-dark', bg: '--color-accent-light', border: '--color-accent' },
};

const cssVar = (name: string) => `var(${name})`;

/** Ready-to-use `var(--…)` values per tone. */
export const TONE_TOKENS: Record<StatusTone, { fg: string; bg: string; border: string }> =
  Object.fromEntries(
    STATUS_TONES.map((t) => {
      const n = TONE_TOKEN_NAMES[t];
      return [t, { fg: cssVar(n.fg), bg: cssVar(n.bg), border: cssVar(n.border) }];
    }),
  ) as Record<StatusTone, { fg: string; bg: string; border: string }>;

/**
 * Default tone per status value, across ELC domains (access requests,
 * fulfilment, workflow instances, catalogs, integrations, role matrix).
 * Decisions where screens used to disagree:
 *   - cancelled, archived, skipped, expired, superseded → neutral
 *     (deliberate or terminal, not a problem to act on)
 *   - draft → info (work in progress; distinct from archived)
 *   - error (connection state) → danger (was warn on Integrations)
 *   - privileged (risk tier) → warning (was danger in the request
 *     wizard, warn in the catalog admin); danger is kept for failures
 */
const STATUS_TONE: Record<string, StatusTone> = {
  // in flight
  pending: 'info',
  running: 'info',
  active: 'success',
  in_progress: 'info',
  draft: 'info',
  assigned: 'info',
  // waiting on someone / needs attention
  queued: 'warning',
  awaiting_it: 'warning',
  needs_reauth: 'warning',
  drift_missing: 'warning',
  drift_detected: 'warning',
  privileged: 'warning',
  // good outcomes
  approved: 'success',
  completed: 'success',
  fired: 'success',
  published: 'success',
  provisioned: 'success',
  returned: 'success',
  // failures
  failed: 'danger',
  rejected: 'danger',
  error: 'danger',
  drift_extra: 'danger',
  lost: 'danger',
  // terminal / inert
  cancelled: 'neutral',
  archived: 'neutral',
  skipped: 'neutral',
  expired: 'neutral',
  revoked_expired: 'neutral',
  superseded: 'neutral',
  revoked: 'neutral',
  retired: 'neutral',
  not_started: 'neutral',
  not_yet: 'neutral',
  standard: 'neutral',
};

/**
 * Per-domain overrides where a status word means something else.
 * `step`: workflow step/instance timeline — `pending` is "not reached
 * yet" (neutral) and `active` is "running now" (info), not "enabled".
 */
export type StatusDomain = string;

const DOMAIN_OVERRIDES: Record<StatusDomain, Record<string, StatusTone>> = {
  step: { pending: 'neutral', active: 'info' },
};

/** Tone for a status value; unknown or empty values are neutral. */
export function statusTone(status: string | null | undefined, domain?: StatusDomain): StatusTone {
  if (!status) return 'neutral';
  const overrides = domain && Object.prototype.hasOwnProperty.call(DOMAIN_OVERRIDES, domain)
    ? DOMAIN_OVERRIDES[domain] : undefined;
  const registry = domain ? registries.get(domain) : undefined;
  return (overrides && Object.prototype.hasOwnProperty.call(overrides, status) ? overrides[status] : undefined)
    ?? (registry && Object.prototype.hasOwnProperty.call(registry, status) ? registry[status]?.tone : undefined)
    ?? (Object.prototype.hasOwnProperty.call(STATUS_TONE, status) ? STATUS_TONE[status] : undefined)
    ?? 'neutral';
}

/** Inline-style colours (text + background) for a tone. */
export function toneStyle(tone: StatusTone): { color: string; background: string } {
  return { color: TONE_TOKENS[tone].fg, background: TONE_TOKENS[tone].bg };
}

export type StatusDefinition = { label: string; tone: StatusTone };
export type StatusRegistry = Readonly<Record<string, StatusDefinition>>;

const labels: Record<string, string> = {
  pending: 'Pending', running: 'Running', active: 'Active', in_progress: 'In progress',
  draft: 'Draft', assigned: 'Assigned', queued: 'Queued', awaiting_it: 'Waiting on IT',
  needs_reauth: 'Needs reconnection', drift_missing: 'Missing access', drift_detected: 'Drift detected',
  privileged: 'Privileged', approved: 'Approved', completed: 'Completed', fired: 'Fired',
  published: 'Published', provisioned: 'Provisioned', returned: 'Returned', failed: 'Failed',
  rejected: 'Rejected', error: 'Error', drift_extra: 'Extra access', lost: 'Lost',
  cancelled: 'Cancelled', archived: 'Archived', skipped: 'Skipped', expired: 'Expired',
  revoked_expired: 'Removed after expiry', superseded: 'Superseded', revoked: 'Revoked',
  retired: 'Retired', not_started: 'Not started', not_yet: 'Not yet', standard: 'Standard',
};

export const STATUS_REGISTRY: StatusRegistry = Object.freeze(Object.fromEntries(
  Object.entries(STATUS_TONE).map(([key, tone]) => [key, Object.freeze({ label: labels[key]!, tone })]),
));
const registries = new Map<string, StatusRegistry>([
  ['worker-type', Object.freeze<StatusRegistry>({
    employee: Object.freeze({ label: 'Employee', tone: 'neutral' }),
    contractor: Object.freeze({ label: 'Contractor', tone: 'accent' }),
    vendor: Object.freeze({ label: 'Vendor', tone: 'accent' }),
    intern: Object.freeze({ label: 'Intern', tone: 'accent' }),
    temp: Object.freeze({ label: 'Temp', tone: 'accent' }),
  })],
  ['employment', Object.freeze<StatusRegistry>({
    pre_hire: Object.freeze({ label: 'Pre-hire', tone: 'info' }),
    onboarding: Object.freeze({ label: 'Onboarding', tone: 'info' }),
    active: STATUS_REGISTRY.active!,
    on_leave: Object.freeze({ label: 'On leave', tone: 'warning' }),
    offboarding: Object.freeze({ label: 'Offboarding', tone: 'warning' }),
    terminated: Object.freeze({ label: 'Terminated', tone: 'neutral' }),
    // Display-only compatibility for historical payloads; ER-11 retires storage/writes.
    inactive: Object.freeze({ label: 'Terminated', tone: 'neutral' }),
  })],
]);
const warned = new Set<string>();

/** Register a namespace synchronously at bootstrap; never overwrite a registration. */
export function registerStatusRegistry(domain: string, registry: StatusRegistry): void {
  if (!domain.trim() || domain === 'step' || registries.has(domain)) {
    throw new Error(`Duplicate or reserved status domain: ${domain}`);
  }
  for (const [key, definition] of Object.entries(registry)) {
    if (Object.prototype.hasOwnProperty.call(STATUS_REGISTRY, key) || !definition.label.trim()
      || !STATUS_TONES.includes(definition.tone)) {
      throw new Error(`Invalid or duplicate status key: ${domain}.${key}`);
    }
  }
  registries.set(domain, Object.freeze(Object.fromEntries(Object.entries(registry)
    .map(([key, definition]) => [key, Object.freeze({ ...definition })]))));
}

export function statusDefinition(status?: string | null, domain?: string): StatusDefinition {
  if (!status?.trim()) return { label: 'Not available', tone: 'neutral' };
  const registry = domain ? registries.get(domain) : undefined;
  const definition = (registry && Object.prototype.hasOwnProperty.call(registry, status) ? registry[status] : undefined)
    ?? (Object.prototype.hasOwnProperty.call(STATUS_REGISTRY, status) ? STATUS_REGISTRY[status] : undefined);
  if (definition) return { ...definition, tone: statusTone(status, domain) };
  const diagnostic = `${domain ?? 'global'}:${status}`;
  if (import.meta.env.DEV && !warned.has(diagnostic)) {
    warned.add(diagnostic);
    console.warn('Unknown status in UI registry', diagnostic);
  }
  return { label: 'Unknown status', tone: 'neutral' };
}
